import React from "react";
import { wormColors } from "core/src/core/constants.js";
import { flagUrl, isFlagCode } from "../../../shared/flags";
import styles from "./RoundOverCard.module.scss";

// The between-rounds strip, shared by local play (GameOver) and online
// (RemoteGame's countdown phase). It runs along the arena's top edge instead of
// covering the middle, so the board and the winner's celebration stay in view:
// trophy, winner and points on the left, the auto-advance countdown as a
// draining ring on the right. The same strip on every layout; on the compact
// one the HUD and score panel step aside for it (GameLayout) and it leaves room
// for the toolbar at its right end.

interface Props {
  winnerName: string;
  winnerColorId?: string;
  winnerFlag?: string | null;
  roundNumber: number;
  // The winner's points from this round; hidden when unknown.
  roundPoints?: number | null;
  final: boolean;
  secondsLeft: number | string;
  // How long the countdown runs in total; the ring drains over it, and the
  // strip fades out with the board's contents over the wait's end.
  totalSeconds?: number;
  // A dialog is holding the countdown: hold the fade-out too.
  paused?: boolean;
  // The match is in a tie-break: the top is shared past the target, so this
  // round didn't settle it. A gold two-line call sits mid-arena for the wait.
  tie?: boolean;
}

const RING_R = 18;
const RING_C = 2 * Math.PI * RING_R;

// The board's contents fade out over the wait's last CONTENT_FADE seconds (see
// GameCanvas `contentFade`), gone a beat before the next round takes the board.
// Not after the last round: the match-end screens sit over that board as it is.
// Any wait length works: a wait shorter than the fade just fades for the
// whole of it.
export const CONTENT_FADE = 1.0;
export const CONTENT_FADE_LEAD = 0.2;
const STRIP_OUT = 0.4; // the strip's slide-out, as quick as its slide-in
export function contentFadeFor(totalSeconds: number, paused = false) {
  const total = Math.max(0, totalSeconds || 0);
  const duration = Math.min(CONTENT_FADE, total);
  return { at: Math.max(0, total - duration - CONTENT_FADE_LEAD), duration, paused };
}

// The strip leaves as quickly as it came (STRIP_OUT, the slide-in's length),
// timed to be gone a beat before the next round takes the board. GameLayout
// times the phone scoreboard's slide-out by it too.
export function stripTiming(totalSeconds: number) {
  const stripOutAt = Math.max(0, totalSeconds - STRIP_OUT - CONTENT_FADE_LEAD);
  return { "--strip-out-at": `${stripOutAt}s`, "--strip-out-dur": `${STRIP_OUT}s` } as React.CSSProperties;
}

export default function RoundOverCard(props: Props) {
  const { winnerName, winnerColorId, winnerFlag, roundNumber, roundPoints, final, secondsLeft, totalSeconds = 5, paused, tie } = props;
  const timing = stripTiming(totalSeconds);
  return (
    <>
    {tie && !final ? (
      // The tie call: a gold-edged band across the middle of the arena, the
      // strip's dark fill, sliding in from the left and leaving with the strip.
      <div className={styles.tie + (paused ? " " + styles.stripPaused : "")} style={timing} aria-live="polite">
        <span className={styles.tieLine1}>We have a tie!</span>
        <span className={styles.tieLine2}>There can only be one winner</span>
      </div>
    ) : null}
    <div
      className={styles.strip + (paused ? " " + styles.stripPaused : "")}
      style={timing}
    >
      <span className={styles.crown}>🏆</span>
      <div className={styles.winner}>
        <span className={styles.who} style={winnerColorId !== undefined ? { color: wormColors[winnerColorId] } : undefined}>
          {isFlagCode(winnerFlag) ? <img className={styles.flag} src={flagUrl(winnerFlag!)} width={22} height={22} alt="" /> : null}
          <span>{winnerName}</span>
        </span>
        <span className={styles.subline}>
          takes round {roundNumber}
          {typeof roundPoints === "number" ? <> · <b>+{roundPoints} points</b></> : null}
        </span>
      </div>
      <div className={styles.count} title={final ? "Final results" : "Next round"}>
        <span className={styles.countLabel}>{final ? "Final results" : "Next round"}</span>
        <span className={styles.ring} style={{ "--ring-secs": `${totalSeconds}s` } as React.CSSProperties}>
          <svg viewBox="0 0 44 44" width="44" height="44" aria-hidden="true">
            <circle cx="22" cy="22" r={RING_R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
            <circle className={styles.ringArc} cx="22" cy="22" r={RING_R} fill="none" strokeWidth="3" strokeLinecap="round" strokeDasharray={RING_C} />
          </svg>
          <b>{secondsLeft}</b>
        </span>
      </div>
    </div>
    </>
  );
}
